<script setup lang="ts">
import { isUnread, setNotifyMessages, useMessaging, type Organisation } from "../composables/useMessaging"
import { t, tn } from "../lib/i18n"
import { organisationLogo } from "../lib/pictures"

// One of many organisations, for someone in dozens or hundreds of shops: a search instead of a row of
// chips. Those with unread conversations come first. allLabel: a first row for "all of them"
// (select null). notifications: each row turns the organisation's message notifications on or off.
const props = defineProps<{
  title: string
  organisations: Pick<Organisation, "id" | "name" | "logo" | "notifyMessages">[]
  selected?: string | null
  allLabel?: string
  notifications?: boolean
}>()
const emit = defineEmits<{ select: [id: string | null]; close: [] }>()
const hub = useMessaging()

/** Rows shown without a search; the search finds the rest */
const SHOWN = 100

const query = ref("")
const search = ref<HTMLInputElement | null>(null)
const failed = ref("")

/** Unread conversations per organisation, counted once */
const unread = computed(() => {
  const counts = new Map<string, number>()
  for (const th of hub.threads) {
    if (!isUnread(th)) continue
    for (const o of th.organisations) counts.set(o.id, (counts.get(o.id) ?? 0) + 1)
  }
  return counts
})

const matching = computed(() => {
  const words = query.value.trim().toLowerCase().split(/\s+/).filter(Boolean)
  const found = props.organisations.filter((o) => words.every((w) => o.name.toLowerCase().includes(w)))
  // Unread first, otherwise as given
  return found
    .map((o, i) => ({ o, i, n: unread.value.get(o.id) ?? 0 }))
    .sort((a, b) => (b.n > 0 ? 1 : 0) - (a.n > 0 ? 1 : 0) || a.i - b.i)
    .map((x) => x.o)
})
const shown = computed(() => matching.value.slice(0, SHOWN))

async function toggle(o: Pick<Organisation, "id" | "notifyMessages">) {
  failed.value = ""
  try {
    await setNotifyMessages(o.id, o.notifyMessages === false)
  } catch {
    failed.value = t("Something went wrong.")
  }
}

onMounted(() => search.value?.focus({ preventScroll: true }))
</script>

<template>
  <HubSheet :title="title" @close="emit('close')">
    <input
      ref="search"
      v-model="query"
      class="hub-field"
      type="search"
      :placeholder="t('Search by name')"
      :aria-label="t('Search by name')"
    />
    <p v-if="failed" class="failed" role="alert">{{ failed }}</p>
    <ul class="orgs">
      <li v-if="allLabel && !query.trim()">
        <button type="button" class="org" :class="{ on: !selected }" @click="emit('select', null)">
          <span class="name">{{ allLabel }}</span>
          <HubIcon v-if="!selected" name="check" :size="20" />
        </button>
      </li>
      <li v-for="o in shown" :key="o.id">
        <button type="button" class="org" :class="{ on: o.id === selected }" @click="emit('select', o.id)">
          <HubAvatar :name="o.name" :id="o.id" :src="organisationLogo(o, 36)" :size="36" />
          <span class="name">{{ o.name }}</span>
          <span v-if="unread.get(o.id)" class="badge">{{ unread.get(o.id) }}</span>
          <HubIcon v-if="o.id === selected" name="check" :size="20" />
        </button>
        <button
          v-if="notifications"
          type="button"
          class="hub-icon-button bell"
          :class="{ off: o.notifyMessages === false }"
          :aria-pressed="o.notifyMessages !== false"
          :aria-label="t('Notifications of new messages: {name}', { name: o.name })"
          @click="toggle(o)"
        >
          <HubIcon :name="o.notifyMessages === false ? 'bell-off' : 'bell'" :size="20" />
        </button>
      </li>
    </ul>
    <p v-if="!matching.length" class="note">{{ t("No matches.") }}</p>
    <p v-else-if="matching.length > shown.length" class="note">
      {{ tn(matching.length - shown.length, "{n} more: search by name.", "{n} more: search by name.") }}
    </p>
  </HubSheet>
</template>

<style scoped>
.orgs {
  list-style: none;
  margin: 4px 0 0;
  padding: 0;
  display: grid;
}

li {
  display: flex;
  align-items: center;
  gap: 4px;
}

.org {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 52px;
  padding: 6px 8px;
  border: 0;
  border-radius: var(--hub-radius-button);
  background: none;
  color: inherit;
  text-align: left;
  font: inherit;
}

.org.on {
  font-weight: 700;
}

.name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.badge {
  min-width: 22px;
  padding: 1px 7px;
  border-radius: 11px;
  background: var(--hub-badge);
  color: var(--hub-on-badge);
  font-size: 0.8rem;
  font-weight: 700;
  text-align: center;
}

.bell.off {
  color: var(--hub-muted);
}

.note,
.failed {
  margin: 8px 8px 0;
  font-size: 0.85rem;
  color: var(--hub-muted);
}

.failed {
  color: var(--hub-danger);
}
</style>
